// Token prompt for mobile clients. When the API rejects requests with 401 (a
// relay-forwarded request with no valid token attached), this
// component blocks the dashboard and lets the user paste a token minted from
// Settings → Network on the desktop — on first load, and again when a token is
// revoked while the page is open (any 401 the probe confirms). On the cloud the
// host's sign-in gate owns that job.
import { useEffect, useRef, useState } from 'react';
import { apiFetch, onUnauthorized, setApiToken } from '@/lib/api';
import { isCloud } from '@/lib/surface';
import { Button } from '@onestone/ui/button';
import { Input } from '@onestone/ui/input';
import { Eye, EyeOff } from 'lucide-react';
import { LoadingState } from '@onestone/ui/loading-state';

type Status = 'checking' | 'ok' | 'needs-token';

/**
 * Phones land here via a pairing QR like
 *   https://relay/d/<deviceId>/#token=atk_xxx
 * Pull the token out of the URL fragment, store it, and clean the bar so it
 * doesn't sit in history.
 */
export function consumeTokenFromUrl(): void {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return;
  const params = new URLSearchParams(hash.includes('=') ? hash : `${hash}=`);
  const value = params.get('token');
  if (!value) return;
  setApiToken(value.trim());
  params.delete('token');
  const remainder = params.toString();
  const cleaned = remainder ? `#${remainder}` : '';
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${cleaned}`);
}

async function probeAuth(): Promise<Status> {
  try {
    const res = await apiFetch(`/api/onboarding/status`);
    if (res.status === 401) return 'needs-token';
    return 'ok';
  } catch {
    return 'ok';
  }
}

export function AuthTokenGate({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<Status>('checking');
  const [pasted, setPasted] = useState('');
  const [error, setError] = useState('');
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    // consumeTokenFromUrl() already ran before React mounted (see main.tsx) so
    // the HashRouter never sees the #token= fragment. Call again here as a
    // no-op fallback in case this component is ever loaded outside that path.
    consumeTokenFromUrl();
    void probeAuth().then(setStatus);
  }, []);

  // A 401 while the dashboard is open: confirm with the probe (one at a time —
  // the probe's own 401 lands here too), then drop the revoked token and show
  // the prompt. Unmounting the dashboard also ends every poll and the
  // live-events stream, which would otherwise retry their 401s for good.
  const probing = useRef(false);
  useEffect(() => {
    if (status !== 'ok' || isCloud()) return;
    return onUnauthorized(() => {
      if (probing.current) return;
      probing.current = true;
      void probeAuth()
        .then((next) => {
          if (next !== 'needs-token') return;
          setApiToken('');
          setStatus('needs-token');
        })
        .finally(() => {
          probing.current = false;
        });
    });
  }, [status]);

  if (status === 'checking') {
    return <LoadingState variant="centered" />;
  }

  if (status === 'ok') return <>{children}</>;

  async function handleSave() {
    setError('');
    const token = pasted.trim();
    if (!token) {
      setError('Paste the token you copied from the desktop.');
      return;
    }
    setApiToken(token);
    const next = await probeAuth();
    if (next === 'ok') {
      setStatus('ok');
    } else {
      setApiToken('');
      setError("That token didn't work. Generate a new one from the desktop and try again.");
    }
  }

  // Detect standalone PWA mode (iOS Add to Home Screen / Android TWA).
  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as { standalone?: boolean }).standalone === true);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-sm">
        <h1 className="text-base font-semibold tracking-tight">Connect to your dashboard</h1>

        {isStandalone ? (
          <p className="mt-2 text-xs text-muted-foreground">
            You&apos;re running from your Home Screen, which has separate storage from Safari. Paste the full{' '}
            <code className="rounded bg-muted px-1 font-mono">atk_…</code> token that was shown when you first paired
            this phone — not the short preview in Settings.
          </p>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">
            On the desktop running OneStone, open{' '}
            <span className="font-medium">Settings → Network &amp; Mobile Access</span>, tap{' '}
            <span className="font-medium">Pair a phone</span>, and paste the full{' '}
            <code className="rounded bg-muted px-1 font-mono">atk_…</code> token shown there.
          </p>
        )}

        <div className="relative mt-4">
          <Input
            type={showToken ? 'text' : 'password'}
            placeholder="atk_..."
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            className="pr-9"
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowToken((v) => !v)}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={showToken ? 'Hide token' : 'Show token'}
          >
            {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

        <Button className="mt-4 w-full" onClick={handleSave}>
          Connect
        </Button>
      </div>
    </div>
  );
}
